import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiCopy } from 'react-icons/bi';
import { FaEdit } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import TooltipOverlay from './tooltip/Tooltip';
import { SuccessToast } from '../../utils/toasters';

interface IEditableCopyFieldProps {
    label: string;
    value?: string | null;
    onChange: (value: string) => void;
    onSave: () => Promise<boolean>;
    tooltip?: string;
    showActions?: boolean;
    canEdit?: boolean;
    className?: string;
}

export function EditableCopyField({
    label,
    value,
    onChange,
    onSave,
    tooltip,
    showActions = true,
    canEdit = true,
    className = 'my-3',
}: IEditableCopyFieldProps) {
    const { t } = useTranslation();
    const [isEdit, setIsEdit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value ?? '');
        SuccessToast(t('general.button.copied'));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const isSaved = await onSave();
        setIsSaving(false);
        if (isSaved) {
            setIsEdit(false);
        }
    };

    return (
        <div className={`d-flex align-items-center ${className}`}>
            <TextField
                label={label}
                variant="outlined"
                value={value ?? ''}
                size="small"
                onChange={e => onChange(e.target.value)}
                disabled={!isEdit}
                className="w-100"
            />
            {tooltip && (
                <TooltipOverlay className="ms-1" content={tooltip}>
                    <FiInfo size={14} />
                </TooltipOverlay>
            )}
            {showActions &&
                (isEdit ? (
                    <Button
                        onClick={handleSave}
                        disabled={!value?.length || isSaving}
                        variant="primary"
                        className="ml-2"
                    >
                        {t('general.button.save')}
                    </Button>
                ) : (
                    (canEdit || !!value) && (
                        <div className="d-flex align-items-center ml-2">
                            {canEdit && (
                                <FaEdit
                                    onClick={() => setIsEdit(true)}
                                    className="font-18 cursor-pointer text-primary"
                                />
                            )}
                            {!!value && (
                                <BiCopy
                                    className={`copy-icon text-muted ${canEdit ? 'ml-2' : ''}`}
                                    onClick={handleCopy}
                                />
                            )}
                        </div>
                    )
                ))}
        </div>
    );
}
